import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Upload,
  FileText,
  FileSpreadsheet,
  FileImage,
  FileArchive,
  Download,
  Eye,
  Trash2,
  Search,
  Calendar,
  RefreshCw,
  Loader2,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const BYTE_UNITS = ["B", "KB", "MB", "GB"];
function fmtSize(bytes) {
  if (!bytes) return "0 B";
  let n = bytes;
  let i = 0;
  while (n >= 1024 && i < BYTE_UNITS.length - 1) {
    n /= 1024;
    i += 1;
  }
  return `${n.toFixed(n >= 10 || i === 0 ? 0 : 1)} ${BYTE_UNITS[i]}`;
}

function fmtDate(d) {
  if (!d) return "—";
  try {
    return new Date(`${d}T00:00:00`).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return d;
  }
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function iconForFile(filename, contentType) {
  const ext = (filename || "").split(".").pop()?.toLowerCase() || "";
  if (["xlsx", "xls", "xlsm", "csv", "tsv"].includes(ext)) return FileSpreadsheet;
  if (["png", "jpg", "jpeg", "webp", "gif"].includes(ext)) return FileImage;
  if (["zip"].includes(ext)) return FileArchive;
  if ((contentType || "").startsWith("image/")) return FileImage;
  return FileText;
}

function todayLocal() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export default function AdminVendorReportsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // Upload form state
  const [files, setFiles] = useState([]);
  const [reportDate, setReportDate] = useState(todayLocal());
  const [label, setLabel] = useState("");
  const [notes, setNotes] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q.trim()) params.q = q.trim();
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const r = await api.get("/admin/vendor-reports", { params });
      setItems(r.data || []);
    } catch (e) {
      toast.error("Couldn't load vendor reports");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [q, dateFrom, dateTo]);

  useEffect(() => {
    load();
  }, [load]);

  const resetForm = () => {
    setFiles([]);
    setLabel("");
    setNotes("");
    setReportDate(todayLocal());
    setUploadProgress({ current: 0, total: 0 });
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeFile = (idx) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const onUpload = async (e) => {
    e?.preventDefault?.();
    if (!files.length) {
      toast.error("Pick at least one file");
      return;
    }
    if (!reportDate) {
      toast.error("Pick a report date");
      return;
    }
    const oversized = files.find((f) => f.size > 25 * 1024 * 1024);
    if (oversized) {
      toast.error(`"${oversized.name}" exceeds 25 MB`);
      return;
    }
    setUploading(true);
    setUploadProgress({ current: 0, total: files.length });
    let succeeded = 0;
    const failures = [];
    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      setUploadProgress({ current: i + 1, total: files.length });
      const fd = new FormData();
      fd.append("file", f);
      fd.append("report_date", reportDate);
      if (label.trim()) fd.append("label", label.trim());
      if (notes.trim()) fd.append("notes", notes.trim());
      try {
        await api.post("/admin/vendor-reports", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        succeeded += 1;
      } catch (err) {
        failures.push({ name: f.name, error: err?.response?.data?.detail || "Upload failed" });
      }
    }
    if (succeeded > 0 && failures.length === 0) {
      toast.success(`Uploaded ${succeeded} report${succeeded === 1 ? "" : "s"}`);
      resetForm();
    } else if (succeeded > 0 && failures.length > 0) {
      toast.error(`${succeeded} uploaded, ${failures.length} failed (${failures[0].name}: ${failures[0].error})`);
      setFiles(files.filter((f) => failures.some((x) => x.name === f.name)));
    } else {
      toast.error(`All ${failures.length} uploads failed: ${failures[0]?.error}`);
    }
    load();
    setUploading(false);
  };

  const onView = async (row) => {
    // Open a placeholder tab SYNCHRONOUSLY while we still hold the user gesture
    // — popup blockers fire when window.open is called after an async gap.
    const newWindow = window.open("about:blank", "_blank");
    if (!newWindow) {
      toast.error("Pop-up blocked. Allow pop-ups for this site, then click View again.");
      return;
    }
    try {
      const r = await api.get(`/admin/vendor-reports/${row.id}/download`, {
        params: { inline: true },
        responseType: "blob",
      });
      const blob = new Blob([r.data], { type: row.content_type || r.data.type });
      const blobUrl = URL.createObjectURL(blob);
      // Navigate the already-open tab to the blob (no second popup needed).
      newWindow.location.replace(blobUrl);
      // Break the opener relationship for security once the tab is loaded.
      try { newWindow.opener = null; } catch { /* cross-origin once blob loads */ }
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    } catch {
      newWindow.close();
      toast.error("Couldn't open the report");
    }
  };

  const onDownload = async (row) => {
    try {
      const r = await api.get(`/admin/vendor-reports/${row.id}/download`, {
        responseType: "blob",
      });
      const blobUrl = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = row.filename || "report";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    } catch {
      toast.error("Download failed");
    }
  };

  const onDelete = async (row) => {
    if (!window.confirm(`Delete report "${row.filename}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/vendor-reports/${row.id}`);
      toast.success("Report deleted");
      setItems((prev) => prev.filter((x) => x.id !== row.id));
    } catch {
      toast.error("Delete failed");
    }
  };

  const grouped = useMemo(() => {
    const map = new Map();
    for (const it of items) {
      const key = it.report_date || "—";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(it);
    }
    return Array.from(map.entries());
  }, [items]);

  return (
    <div
      className="px-2 sm:px-3 md:px-6 py-4 md:py-6 space-y-5"
      data-testid="admin-vendor-reports-page"
    >
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight">
            Vendor reports
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Upload daily reports from your card vendors (Excel, Word, PDF, CSV, ZIP — any format).
            Stored privately. Open or download any day.
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          data-testid="vendor-reports-refresh"
          className="inline-flex items-center gap-2 h-11 px-4 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-sm font-semibold text-slate-700 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {/* Upload card */}
      <form
        onSubmit={onUpload}
        className="bg-white rounded-2xl border border-slate-200 p-4 sm:p-5 space-y-4"
        data-testid="vendor-reports-upload-form"
      >
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
          <Upload className="w-4 h-4 text-emerald-600" />
          Upload a new report
        </div>

        <div className="grid sm:grid-cols-12 gap-3">
          {/* Date */}
          <div className="sm:col-span-3">
            <label className="block text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1.5">
              Report date
            </label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              <input
                type="date"
                value={reportDate}
                onChange={(e) => setReportDate(e.target.value)}
                required
                data-testid="vendor-report-date"
                className="w-full h-11 pl-9 pr-3 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-slate-400 outline-none text-sm"
              />
            </div>
          </div>

          {/* Label */}
          <div className="sm:col-span-5">
            <label className="block text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1.5">
              Label / vendor (optional)
            </label>
            <input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g. Vendor A — daily settlement"
              maxLength={200}
              data-testid="vendor-report-label"
              className="w-full h-11 px-3 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-slate-400 outline-none text-sm"
            />
          </div>

          {/* File */}
          <div className="sm:col-span-4">
            <label className="block text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1.5">
              Files (max 25 MB each)
            </label>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={(e) => setFiles(Array.from(e.target.files || []))}
              data-testid="vendor-report-file"
              className="w-full text-sm file:mr-3 file:py-2.5 file:px-3 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-slate-900 file:text-white hover:file:bg-slate-800 file:cursor-pointer cursor-pointer h-11 leading-[2.4rem]"
            />
          </div>

          {/* Notes */}
          <div className="sm:col-span-12">
            <label className="block text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1.5">
              Notes (optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any remarks for future reference…"
              rows={2}
              maxLength={2000}
              data-testid="vendor-report-notes"
              className="w-full px-3 py-2.5 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-slate-400 outline-none text-sm resize-y"
            />
          </div>
        </div>

        {/* Selected files list */}
        {files.length > 0 && (
          <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3 space-y-1.5" data-testid="vendor-report-file-list">
            <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">
              {files.length} file{files.length === 1 ? "" : "s"} selected
            </div>
            <ul className="space-y-1">
              {files.map((f, idx) => (
                <li key={`${f.name}-${idx}`} className="flex items-center gap-2 text-xs text-slate-700">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" />
                  <span className="font-mono truncate flex-1">{f.name}</span>
                  <span className="text-slate-400">{fmtSize(f.size)}</span>
                  {!uploading && (
                    <button
                      type="button"
                      onClick={() => removeFile(idx)}
                      aria-label={`Remove ${f.name}`}
                      className="w-5 h-5 inline-flex items-center justify-center rounded text-slate-400 hover:text-rose-600 hover:bg-rose-50"
                    >
                      ×
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex items-center justify-between gap-3">
          {files.length === 0 ? (
            <div className="flex items-center gap-2 text-xs text-slate-400">
              <AlertCircle className="w-4 h-4" />
              No files selected
            </div>
          ) : uploading ? (
            <div className="flex items-center gap-2 text-xs text-slate-600">
              <Loader2 className="w-4 h-4 animate-spin text-emerald-600" />
              Uploading {uploadProgress.current} of {uploadProgress.total}…
            </div>
          ) : (
            <div className="text-xs text-slate-500">
              Each file uploaded as a separate report sharing the same date / label / notes.
            </div>
          )}
          <button
            type="submit"
            disabled={uploading || files.length === 0}
            data-testid="vendor-report-submit"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {uploading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Upload className="w-4 h-4" />
            )}
            {uploading
              ? `Uploading ${uploadProgress.current}/${uploadProgress.total}…`
              : files.length > 1
                ? `Upload ${files.length} reports`
                : "Upload report"}
          </button>
        </div>
      </form>

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-slate-200 p-3 sm:p-4 flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[220px]">
          <label className="block text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1.5">
            Search
          </label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="filename, label, notes, uploader…"
              data-testid="vendor-reports-search"
              className="w-full h-11 pl-10 pr-4 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-slate-400 outline-none text-sm"
            />
          </div>
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1.5">
            From
          </label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            data-testid="vendor-reports-date-from"
            className="h-11 px-3 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-slate-400 outline-none text-sm"
          />
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1.5">
            To
          </label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            data-testid="vendor-reports-date-to"
            className="h-11 px-3 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-slate-400 outline-none text-sm"
          />
        </div>
        {(q || dateFrom || dateTo) && (
          <button
            onClick={() => {
              setQ("");
              setDateFrom("");
              setDateTo("");
            }}
            className="h-11 px-3 text-sm font-semibold text-slate-600 hover:text-slate-900"
            data-testid="vendor-reports-clear-filters"
          >
            Clear
          </button>
        )}
      </div>

      {/* List */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-sm text-slate-500" data-testid="vendor-reports-loading">
            <Loader2 className="w-6 h-6 mx-auto animate-spin text-slate-400" />
            <div className="mt-2">Loading reports…</div>
          </div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center" data-testid="vendor-reports-empty">
            <FileText className="w-10 h-10 mx-auto text-slate-300" />
            <p className="mt-3 text-slate-500 text-sm">
              No reports yet. Upload your first vendor report above.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {grouped.map(([date, rows]) => (
              <div key={date}>
                <div className="px-4 sm:px-5 py-2.5 bg-slate-50/70 border-b border-slate-100">
                  <div className="flex items-center justify-between">
                    <div className="text-xs uppercase tracking-[0.16em] font-bold text-slate-700">
                      {fmtDate(date)}
                    </div>
                    <div className="text-[11px] text-slate-500">
                      {rows.length} {rows.length === 1 ? "report" : "reports"}
                    </div>
                  </div>
                </div>
                <ul className="divide-y divide-slate-100">
                  {rows.map((row, i) => {
                    const Icon = iconForFile(row.filename, row.content_type);
                    return (
                      <li
                        key={row.id}
                        data-testid={`vendor-report-row-${i}`}
                        className="px-3 sm:px-5 py-3.5 flex items-start gap-3 hover:bg-slate-50/60"
                      >
                        <div className="w-10 h-10 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-700 flex items-center justify-center flex-shrink-0">
                          <Icon className="w-5 h-5" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                            <span className="font-semibold text-slate-900 text-sm truncate max-w-[280px] sm:max-w-[420px]">
                              {row.filename}
                            </span>
                            <span className="text-[11px] text-slate-400">
                              {fmtSize(row.size)}
                            </span>
                          </div>
                          {row.label && (
                            <div className="text-xs text-slate-600 mt-0.5 truncate max-w-[420px]">
                              {row.label}
                            </div>
                          )}
                          {row.notes && (
                            <div className="text-[11px] text-slate-500 mt-0.5 italic line-clamp-2">
                              {row.notes}
                            </div>
                          )}
                          <div className="text-[11px] text-slate-400 mt-1">
                            Uploaded {fmtDateTime(row.uploaded_at)}
                            {row.uploaded_by ? ` · by ${row.uploaded_by}` : ""}
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          <button
                            onClick={() => onView(row)}
                            data-testid={`vendor-report-view-${i}`}
                            className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-slate-300"
                            aria-label="View report"
                            title="View"
                          >
                            <Eye className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => onDownload(row)}
                            data-testid={`vendor-report-download-${i}`}
                            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold"
                          >
                            <Download className="w-3.5 h-3.5" />
                            <span className="hidden sm:inline">Download</span>
                          </button>
                          <button
                            onClick={() => onDelete(row)}
                            data-testid={`vendor-report-delete-${i}`}
                            className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-slate-200 text-rose-600 hover:bg-rose-50 hover:border-rose-200"
                            aria-label="Delete report"
                            title="Delete"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        )}
      </div>

      <p className="text-xs text-slate-400 px-1">
        Files are stored privately. Only logged-in admins can download.
      </p>
    </div>
  );
}
